import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  X,
  Send,
  RotateCcw,
  Phone,
  ChevronDown,
  CarFront,
  CalendarDays,
  Handshake,
  MapPin,
} from "lucide-react";
import { IMAGES, telLink, waLink } from "@/config";
import type { CfaoVehicle } from "@/data/cfaoVehicles";
import { useVehicles } from "@/hooks/useVehicles";
import { publicVehicles } from "@/utils/vehicleSearch";

interface Message {
  id: string;
  sender: "user" | "assistant";
  text: string;
  timestamp: string;
}

interface StoredSession {
  messages: Message[];
  timestamp: number;
}

const SESSION_STORAGE_KEY = "simphiwe_ai_chat_session_v2";
const ONE_HOUR_MS = 60 * 60 * 1000; // 1 hour in milliseconds
const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:8000" : "");

const WELCOME_MESSAGE = "Hi, I’m Simphiwe’s website assistant. I can help with our vehicles, prices, finance, trade-ins or viewings. What would you like to know?";
const RESTART_MESSAGE = "Hi again. What would you like to know about our vehicles or services?";

const conversationalReply = (text: string): string | null => {
  const cleaned = text.toLowerCase().replace(/[^a-z\s']/g, " ").replace(/\s+/g, " ").trim();
  if (/^(hi|hello|hey|howzit|hiya|good day|good morning|good afternoon|good evening)$/.test(cleaned)) {
    return "Hi! How can I help?";
  }
  if (/^(how are you|how are you doing|how's it going|hows it going)$/.test(cleaned)) {
    return "I’m good, thanks. How can I help?";
  }
  if (/^(thanks|thank you|thank you so much|cheers)$/.test(cleaned)) {
    return "You’re welcome. Is there anything else you’d like to know?";
  }
  if (/^(bye|goodbye|see you|chat later)$/.test(cleaned)) {
    return "Thanks for chatting. You’re welcome back anytime.";
  }
  return null;
};

const buildWebsiteContext = (question: string, PUBLIC_VEHICLES: CfaoVehicle[]) => {
  const query = question.toLowerCase();
  const tokens = query.split(/[^a-z0-9]+/).filter((token) => token.length > 1);
  const currentSlug = window.location.pathname.split("/").pop() || "";
  const makeCounts = PUBLIC_VEHICLES.reduce<Record<string, number>>((counts, vehicle) => {
    counts[vehicle.make] = (counts[vehicle.make] || 0) + 1;
    return counts;
  }, {});
  let ranked = PUBLIC_VEHICLES.map((vehicle) => {
    const searchable = `${vehicle.fullTitle} ${vehicle.model} ${vehicle.make} ${vehicle.bodyType} ${vehicle.fuelType} ${vehicle.transmission} ${vehicle.color}`.toLowerCase();
    const slug = vehicle.url.split("/").pop() || "";
    const score = tokens.reduce((total, token) => total + (searchable.includes(token) ? 1 : 0), 0) + (slug === currentSlug ? 20 : 0);
    return { vehicle, score };
  }).sort((a, b) => b.score - a.score || a.vehicle.price - b.vehicle.price);
  if (/most expensive|highest price|top price/.test(query)) {
    ranked = ranked.sort((a, b) => b.score - a.score || b.vehicle.price - a.vehicle.price);
  }
  const matches = ranked.filter(({ score }) => score > 0).slice(0, 12);
  const selected = matches.length ? matches : ranked.slice(0, 8);
  const vehicleLines = selected.map(({ vehicle }) => {
    const slug = vehicle.url.split("/").pop();
    return `- ${vehicle.fullTitle}; R ${vehicle.price.toLocaleString("en-ZA")}; ${vehicle.mileage.toLocaleString("en-ZA")} km; ${vehicle.transmission}; ${vehicle.fuelType}; ${vehicle.bodyType}; ${vehicle.color}; ${vehicle.seats || "unspecified"} seats; stock ${vehicle.id}; /vehicles/${slug}/`;
  });

  return [
    `Current page: ${window.location.pathname}`,
    `Current stock: ${PUBLIC_VEHICLES.length} listings. Listings by make: ${Object.entries(makeCounts).map(([make, count]) => `${make} ${count}`).join(", ")}.`,
    "Relevant current vehicle listings:",
    ...vehicleLines,
    "Only describe a vehicle as available when it appears in these current listings. Ask Simphiwe to confirm final availability, specifications, condition, service history and warranty.",
  ].join("\n");
};

const STARTER_QUESTIONS = [
  {
    label: "Available Toyotas in stock",
    icon: CarFront,
    query: "What pre-owned Toyotas do you currently have available in stock?",
  },
  {
    label: "Book a test drive",
    icon: CalendarDays,
    query: "How do I book a vehicle viewing or test drive?",
  },
  {
    label: "Car finance & trade-ins",
    icon: Handshake,
    query: "Can you explain the car finance and trade-in options available?",
  },
  {
    label: "Toyota Tokai location",
    icon: MapPin,
    query: "Where is Toyota Tokai located and what are your operating hours?",
  },
];

async function requestAiCompletion(
  apiMessages: { role: "user" | "assistant"; content: string }[],
  question: string,
  vehicles: CfaoVehicle[],
): Promise<string> {
  const response = await fetch(`${API_BASE.replace(/\/$/, "")}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages: apiMessages,
      website_context: buildWebsiteContext(question, vehicles),
    }),
  });
  if (!response.ok) throw new Error(`Chat request failed: ${response.status}`);
  const data = await response.json();
  if (!data.success || !data.reply) throw new Error("Chat response was empty");
  return String(data.reply).trim();
}

export const ChatBot: React.FC = () => {
  const { vehicles } = useVehicles();
  const publicInventory = publicVehicles(vehicles);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNotificationBadge, setShowNotificationBadge] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const greetingRequested = useRef(false);

  // Load session from sessionStorage with 1-hour expiration check
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      // Clean up legacy keys if any
      sessionStorage.removeItem("simphiwe_chat_messages");
      sessionStorage.removeItem("simphiwe_ai_chat_session");

      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return [];
      const session: StoredSession = JSON.parse(raw);
      if (!session || !session.timestamp || Date.now() - session.timestamp > ONE_HOUR_MS) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return [];
      }
      return Array.isArray(session.messages) ? session.messages : [];
    } catch {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return [];
    }
  });

  // Save session with timestamp on update
  useEffect(() => {
    if (messages.length > 0) {
      const session: StoredSession = {
        messages,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    }
  }, [messages]);

  // Periodic check: clear session if 1 hour has elapsed since last activity
  useEffect(() => {
    const checkExpiry = () => {
      try {
        const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (raw) {
          const session: StoredSession = JSON.parse(raw);
          if (session?.timestamp && Date.now() - session.timestamp > ONE_HOUR_MS) {
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            setMessages([]);
            greetingRequested.current = false;
          }
        }
      } catch {
        // ignore
      }
    };

    const interval = setInterval(checkExpiry, 60 * 1000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const showGreeting = useCallback((restart = false) => {
    setMessages([{
      id: `assistant-${Date.now()}`,
      sender: "assistant",
      text: restart ? RESTART_MESSAGE : WELCOME_MESSAGE,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    }]);
  }, []);

  // When chat is opened and has no messages, generate the AI greeting automatically
  useEffect(() => {
    if (isOpen) {
      setShowNotificationBadge(false);
      setTimeout(() => inputRef.current?.focus(), 150);

      if (messages.length === 0 && !greetingRequested.current) {
        greetingRequested.current = true;
        showGreeting();
      }
    }
  }, [isOpen, messages.length, showGreeting]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen, isLoading]);

  const handleSend = async (userQuestion?: string) => {
    const textToSend = (userQuestion || input).trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");

    const immediateReply = conversationalReply(textToSend);
    if (immediateReply) {
      setMessages([
        ...updatedMessages,
        {
          id: `assistant-${Date.now() + 1}`,
          sender: "assistant",
          text: immediateReply,
          timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
      return;
    }

    setIsLoading(true);

    try {
      const apiMessages = updatedMessages.slice(-8).map((m) => ({
          role: m.sender === "user" ? ("user" as const) : ("assistant" as const),
          content: m.text,
        }));

      const aiResponse = await requestAiCompletion(apiMessages, textToSend, publicInventory);

      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          sender: "assistant",
          text: aiResponse,
          timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          sender: "assistant",
          text: "I'm having a temporary connection issue reaching the AI service. Please try again or reach out directly to Simphiwe on 068 103 1545.",
          timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetChat = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    greetingRequested.current = true;
    showGreeting(true);
  };

  // Render markdown links and bold formatting without opening links in new tabs
  const renderMessageContent = (text: string) => {
    const lines = text.split("\n");
    return lines.map((line, lineIdx) => {
      // Match markdown links [label](url)
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const parts: (string | React.ReactNode)[] = [];
      let lastIndex = 0;
      let match;

      while ((match = linkRegex.exec(line)) !== null) {
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }
        const label = match[1];
        const url = match[2];

        // All internal or external links open in the SAME tab (target="_self")
        if (url.startsWith("/")) {
          parts.push(
            <Link
              key={match.index}
              to={url}
              target="_self"
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-1 font-semibold text-red-600 underline hover:text-red-700"
            >
              {label}
            </Link>
          );
        } else {
          parts.push(
            <a
              key={match.index}
              href={url}
              target="_self"
              className="inline-flex items-center gap-1 font-semibold text-red-600 underline hover:text-red-700"
            >
              {label}
            </a>
          );
        }
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }

      // Render bold tokens in string parts
      const formattedParts = parts.map((part, pIdx) => {
        if (typeof part !== "string") return part;
        const boldRegex = /\*\*([^*]+)\*\*/g;
        const boldParts: (string | React.ReactNode)[] = [];
        let bLast = 0;
        let bMatch;
        while ((bMatch = boldRegex.exec(part)) !== null) {
          if (bMatch.index > bLast) {
            boldParts.push(part.substring(bLast, bMatch.index));
          }
          boldParts.push(
            <strong key={`b-${bMatch.index}`} className="font-bold text-slate-900">
              {bMatch[1]}
            </strong>
          );
          bLast = bMatch.index + bMatch[0].length;
        }
        if (bLast < part.length) {
          boldParts.push(part.substring(bLast));
        }
        return <React.Fragment key={pIdx}>{boldParts}</React.Fragment>;
      });

      return (
        <React.Fragment key={lineIdx}>
          {line.startsWith("- ") || line.startsWith("* ") ? (
            <div className="flex items-start gap-2 my-0.5">
              <span className="text-red-500 font-bold leading-5">•</span>
              <span className="flex-1">{formattedParts}</span>
            </div>
          ) : (
            <p className={lineIdx > 0 ? "mt-1.5" : ""}>{formattedParts}</p>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <aside aria-label="Customer support assistant" className="chatbot-root">
      {/* Floating Action Button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-red-600 hover:bg-red-700 text-white shadow-[0_10px_28px_rgba(220,38,38,0.4)] transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-red-200 sm:bottom-6 sm:right-6"
        aria-label={isOpen ? "Close AI Chatbot" : "Open AI Chatbot"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="h-6 w-6 transition-transform duration-200" />
        ) : (
          <div className="relative flex items-center justify-center">
            <MessageSquare className="h-6 w-6" strokeWidth={2.2} />
            {showNotificationBadge && (
              <span className="absolute -top-1.5 -right-1.5 flex h-3 w-3 rounded-full bg-white ring-2 ring-red-600" />
            )}
          </div>
        )}
      </button>

      {/* Chat Window Panel */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-4 z-50 flex h-[580px] max-h-[calc(100svh-7rem)] w-[390px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-slate-200/90 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.22)] ring-1 ring-black/5 transition-all duration-200 sm:right-6"
          role="dialog"
          aria-label="Chat with Simphiwe Motors AI"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-900 via-slate-950 to-red-950 px-4 py-3.5 text-white">
            <div className="flex items-center gap-3">
              <div className="relative flex h-10 w-10 items-center justify-center overflow-visible rounded-full bg-white shadow-inner ring-2 ring-white/20">
                <img src={IMAGES.brandLogo} alt="" className="h-9 w-9 rounded-full object-contain" width="36" height="36" />
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold tracking-tight">Simphiwe Motors AI</h3>
                <p className="flex items-center gap-1.5 text-[11px] text-slate-300">
                  <span>Toyota Tokai Assistant</span>
                  <span className="inline-block h-1 w-1 rounded-full bg-slate-400" />
                  <span className="text-emerald-400 font-medium">Online</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={resetChat}
                title="Restart conversation"
                className="rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Clear chat"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                title="Close chat"
                className="rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Close"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Quick Contact Bar */}
          <div className="flex items-center justify-between bg-red-50/80 px-3.5 py-1.5 text-[11px] font-medium text-red-900 border-b border-red-100">
            <span>Talk to Simphiwe directly:</span>
            <div className="flex items-center gap-2 font-bold text-red-600">
              <a href={telLink} target="_self" className="hover:underline flex items-center gap-1">
                <Phone className="h-3 w-3" /> Call
              </a>
              <span>·</span>
              <a href={waLink()} target="_self" className="hover:underline">
                WhatsApp
              </a>
            </div>
          </div>

          {/* Messages Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-slate-50/50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.sender === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-xs leading-relaxed shadow-sm ${
                    msg.sender === "user"
                      ? "bg-red-600 text-white rounded-br-xs"
                      : "bg-white text-slate-700 border border-slate-200/80 rounded-bl-xs"
                  }`}
                >
                  {renderMessageContent(msg.text)}
                </div>
                <span className="mt-1 px-1 text-[10px] text-slate-400">{msg.timestamp}</span>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-xs bg-white border border-slate-200/80 px-4 py-3 w-20 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-red-500 animate-bounce [animation-delay:-0.3s]" />
                <span className="h-2 w-2 rounded-full bg-red-500 animate-bounce [animation-delay:-0.15s]" />
                <span className="h-2 w-2 rounded-full bg-red-500 animate-bounce" />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions (available when 1 message or less) */}
          {messages.length <= 1 && (
            <div className="border-t border-slate-100 bg-white px-3 py-2.5">
              <p className="mb-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Suggested questions</p>
              <div className="flex flex-wrap gap-1.5">
                {STARTER_QUESTIONS.map(({ label, icon: Icon, query }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => handleSend(query)}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:border-red-300 hover:bg-red-50/70 hover:text-red-700 transition-colors disabled:opacity-50"
                  >
                    <Icon className="h-3.5 w-3.5 text-red-600 shrink-0" strokeWidth={2} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Box */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="border-t border-slate-200 bg-white p-3"
          >
            <div className="relative flex items-center">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about cars, prices, finance, viewings..."
                disabled={isLoading}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/70 py-2.5 pl-3.5 pr-11 text-xs text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-100"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="absolute right-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-red-600 text-white transition-opacity hover:bg-red-700 disabled:opacity-40 disabled:hover:bg-red-600"
                aria-label="Send message"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1.5 text-center">
              <span className="text-[10px] text-slate-400">
                Powered by Groq AI · Answers may vary · Verify details with Simphiwe
              </span>
            </div>
          </form>
        </div>
      )}
    </aside>
  );
};

export default ChatBot;
